import { motion } from "framer-motion";

const GOLD = "#D4AF37";
const LILAC = "#A78BFA";

function wavePath(width, height, cycles, amp, phase = 0) {
  const mid = height / 2;
  const pts = [];
  for (let x = 0; x <= width; x += 8) {
    const t = (x / width) * Math.PI * 2 * cycles + phase;
    const env = Math.sin((x / width) * Math.PI);
    pts.push(`${x},${(mid + Math.sin(t) * amp * env + Math.sin(t * 2.7) * amp * 0.25 * env).toFixed(1)}`);
  }
  return `M${pts.join(" L")}`;
}

export function Waveform({ className = "", color = GOLD, strands = 4, delay = 0 }) {
  return (
    <svg viewBox="0 0 800 200" fill="none" className={className} aria-hidden preserveAspectRatio="none">
      {Array.from({ length: strands }).map((_, i) => (
        <motion.path
          key={i}
          d={wavePath(800, 200, 2.2 + i * 0.35, 60 - i * 9, i * 0.9)}
          stroke={color}
          strokeWidth={1.4 - i * 0.2}
          strokeOpacity={0.95 - i * 0.18}
          initial={{ pathLength: 0, opacity: 0 }}
          whileInView={{ pathLength: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 2.2, ease: [0.16, 1, 0.3, 1], delay: delay + i * 0.15 }}
        />
      ))}
    </svg>
  );
}

export function EqCurve({ className = "", dark = true }) {
  const grid = dark ? "rgba(255,255,255,0.08)" : "rgba(15,14,19,0.08)";
  return (
    <svg viewBox="0 0 400 160" fill="none" className={className} aria-hidden>
      {[20, 50, 80, 110, 140].map((y) => <line key={y} x1="0" x2="400" y1={y} y2={y} stroke={grid} strokeDasharray="2 6" />)}
      {[60, 130, 200, 270, 340].map((x) => <line key={x} y1="0" y2="160" x1={x} x2={x} stroke={grid} />)}
      <motion.path d="M0 96 C60 96 80 40 130 44 C170 48 190 118 240 112 C290 106 300 60 345 62 C375 64 390 84 400 86"
        stroke={LILAC} strokeWidth="1.8" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }} />
      <motion.path d="M0 100 C90 100 110 130 160 128 C220 126 250 72 300 74 C350 76 380 98 400 100"
        stroke={GOLD} strokeWidth="1.2" strokeOpacity="0.8" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 1.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }} />
      {[[130, 44], [240, 112], [345, 62]].map(([x, y]) => (
        <g key={x}><circle cx={x} cy={y} r="5" fill="#0F0E13" stroke={LILAC} strokeWidth="1.5" /><circle cx={x} cy={y} r="1.6" fill={GOLD} /></g>
      ))}
    </svg>
  );
}

export function SpectrumBars({ count = 28, className = "", color = "bg-gold/70" }) {
  return (
    <div className={`flex items-end gap-[3px] ${className}`} aria-hidden>
      {Array.from({ length: count }).map((_, i) => (
        <span key={i} className={`wave-line w-[3px] rounded-full ${color}`} style={{ height: `${10 + Math.abs(Math.sin(i * 0.7)) * 28 + (i % 3) * 4}px`, animationDelay: `${i * 0.09}s` }} />
      ))}
    </div>
  );
}

export function VerticalLabel({ children, className = "" }) {
  return (
    <p className={`font-mono text-[10px] uppercase tracking-[0.45em] [writing-mode:vertical-rl] rotate-180 ${className}`} aria-hidden>{children}</p>
  );
}
